import type {
  DreamJobItemFragment,
  OccupiedJobFragment,
} from '@xing-com/crate-dream-machine-graphql';
import type { JobCardData } from '@xing-com/crate-dream-machine-types';

export const serialize = (
  dreamJobsCollection:
    | (DreamJobItemFragment | undefined | null)[]
    | undefined
    | null,
  jobWishesPreference: string[]
): JobCardData[] => {
  if (!dreamJobsCollection) {
    return [];
  }

  const data =
    dreamJobsCollection
      .filter((item) => !!item)
      .map((item) => serializeItem(item, jobWishesPreference))
      .filter((item) => item !== null) || [];
  return data;
};

export const serializeOccupiedJob = (
  job: OccupiedJobFragment,
  jobWishesPreference: string[]
): JobCardData => {
  const members =
    job.similarMembers?.filter((member) => member?.user?.id !== undefined) ||
    [];

  const skillsMatched =
    job.skillsMatched
      ?.map((skill) => skill?.skill)
      .filter((item) => item !== undefined && item !== null) || [];

  const skillsNotMatched =
    job.skillsNotMatched
      ?.map((skill) => skill?.skill)
      .filter((item) => item !== undefined && item !== null) || [];

  return {
    __typename: 'JobBasketCardData',
    type: 'TakenJob',
    createdAt: new Date().toISOString(),
    id: job.id,
    coverImage: job.company?.entityPage?.coverImage?.[0].url,
    similarMembers: members,
    similarMembersCount: members.length,
    jobTitle: job.jobTitle,
    jobSlug: null,
    companyLogo: job.company?.logos?.logo256px,
    company: job.company,
    matchJobPreferredIndustry: jobWishesPreference?.includes(
      job.company?.industry?.localizationValue || ''
    ),
    fallbackCompany: job.fallbackCompany,
    skillsMatched,
    skillsMatchedCount: job.skillsMatchedCount,
    skillsNotMatched,
    skillsNotMatchedCount: job.skillsNotMatchedCount,
    skillsTotal: job.skillsTotal,
    salary: job.salary,
    location: job.company?.address?.city || job.fallbackCompany?.address?.city,
    similiarMembersVompWhitelist:
      job?.vompMembers
        ?.map((vomp) => vomp?.userId)
        .filter((item) => item !== null && item !== undefined) || [],
  };
};

const serializeItem = (
  item: DreamJobItemFragment,
  jobWishesPreference: string[]
): JobCardData | null => {
  if (
    item?.__typename === 'TakenJob' &&
    item.job?.__typename === 'OccupiedJob'
  ) {
    return {
      urn: item.takenJobUrn,
      trackingToken: item.takenJobTrackingToken,
      ...serializeOccupiedJob(item.job, jobWishesPreference),
      createdAt: item.createdAt || new Date().toISOString(),
      type: 'TakenJob',
      __typename: 'JobBasketCardData',
    };
  }

  if (
    item?.__typename === 'AvailableJob' &&
    item.job?.__typename === 'VisibleJob'
  ) {
    // we ignore cards with company null
    if (item.job.companyInfo.company === null) {
      return null;
    }

    const skillsMatched =
      item.job.userProfileMatches?.skills?.matchedSkills
        ?.map((item) => item?.label)
        .filter((item) => item !== undefined) || [];

    const skillsNotMatched =
      item.job.userProfileMatches?.skills?.notMatchedSkills
        ?.map((item) => item?.label)
        .filter((item) => item !== undefined) || [];

    return {
      __typename: 'JobBasketCardData',
      type: 'AvailableJob',
      createdAt: item.createdAt || new Date().toISOString(),
      id: item.job.visibleJobId,
      urn: item.availableJobUrn,
      trackingToken: item.availableJobTrackingToken,
      coverImage: item.job.companyInfo.company?.entityPage?.coverImage?.[0].url,
      similarMembers: null,
      similarMembersCount: null,
      jobTitle: item.job.title,
      jobSlug: item.job.slug,
      company: item.job.companyInfo.company,
      matchJobPreferredIndustry: jobWishesPreference?.includes(
        item.job?.companyInfo.company?.industry?.localizationValue || ''
      ),
      companyLogo: item.job.companyInfo.company?.logos?.logo256px,
      fallbackCompany: null,
      skillsMatched,
      paid: item.job.paid,
      employmentType: item.job.employmentType,
      skillsMatchedCount: skillsMatched.length || 0,
      skillsNotMatched,
      skillsNotMatchedCount: skillsNotMatched.length || 0,
      skillsTotal: skillsMatched.length + skillsNotMatched.length,
      salary: item.job.salary,
      location:
        item.job.availableJobLocation?.city ||
        item.job.companyInfo?.company?.address?.city,
      userInteractions: item.job.userInteractions,
      refreshedAt: item.job.refreshedAt,
      matchingHighlights: item.job.matchingHighlights,
    };
  }

  return null;
};
